import {
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  MessageBar,
  RoleGuard,
  Table,
  usePermission,
  useTable,
} from 'portal-commons';
import React, { useMemo, FunctionComponent, useState } from 'react';
import { createPortal } from 'react-dom';
import { faFileLock, faTrash, faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { LoaPreviewModal } from '@/components';
import { DetailsSection, Divider } from './components';
import { MaxSenderIdCount } from './constants';
import { getBadgeTypeByStatus } from '../../utils';
import {
  Loa,
  SenderId,
  Trademark,
  brandController,
  senderIdController,
  Brand,
} from '@/apis/core';
import MoreButton from '@/shared_elements/components/MoreButton';
import { generateAndDownloadFile } from '@/utils/download';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';
import { toastFlashMessage } from '@/utils';

interface SenderIdTabProps {
  readOnly?: boolean;
  brandUid: string;
  brand?: Brand;
  trademarks: Trademark[];
  onSenderIdAdd?: (maxCount: number) => void;
  onEditClick?: (senderId: SenderId) => void;
  reloadTrademarks?: () => void;
}

enum PreviewType {
  Preview = 'preview',
  Send = 'send',
}

const SenderIdsTableHeader = [
  { id: 'senderId', label: 'Sender ID to allow', sortable: false },
  {
    id: 'protectedSenderIdList',
    label: 'SENDER ID(S) TO BLOCK',
    sortable: false,
  },
  { id: 'trademark', label: 'Trademark', sortable: false },
  { id: 'associatedDcaList', label: 'Associated DCAs', sortable: false },
];

const LoasTableHeader = [
  { id: 'fileName', label: 'Document', sortable: false },
  { id: 'status', label: 'Status', sortable: false },
  { id: 'createDate', label: 'Create Date', sortable: false },
  { id: 'completionDate', label: 'Completion Date', sortable: false },
];

const SenderIdTab: FunctionComponent<SenderIdTabProps> = ({
  readOnly = false,
  brandUid,
  trademarks,
  onSenderIdAdd,
  onEditClick,
  reloadTrademarks,
  brand,
}) => {
  const {
    loading: senderIdsLoading,
    tableData: senderIds,
    reload: reloadSenderIds,
  } = useTable<SenderId>(
    async () => {
      const records =
        brandUid && !readOnly
          ? await senderIdController.getSenderIds(brandUid)
          : [];
      return {
        data: records,
        total: records.length,
        page: 1,
        rowsPerPage: Math.max(records.length, 10),
      };
    },
    { deps: [brandUid], inMemory: true }
  );
  const [deletingSenderId, setDeletingSenderId] = useState<SenderId | null>(
    null
  );
  const {
    loading: loasLoading,
    tableData: loas,
    reload: reloadLoas,
  } = useTable<Loa>(
    async () => {
      const records =
        brandUid && !readOnly ? await brandController.getLoas(brandUid) : [];
      return {
        data: records,
        total: records.length,
        page: 1,
        rowsPerPage: Math.max(records.length, 10),
      };
    },
    { deps: [brandUid], inMemory: true }
  );
  const [senderIdsProceeding, setSenderIdsProceeding] = useState(false);
  const [loasProceeding, setLoasProceeding] = useState(false);
  const [previewLoa, setPreviewLoa] = useState<Loa>();
  const [previewType, setPreviewType] = useState(PreviewType.Preview);
  const { hasPermission } = usePermission();

  const deleteSenderId = async () => {
    if (!deletingSenderId || senderIdsProceeding) return;
    const hasTrademark = !!deletingSenderId.trademarkUid;
    setSenderIdsProceeding(true);
    setDeletingSenderId(null);
    const response = await senderIdController.deleteSenderId(
      brandUid,
      deletingSenderId.uid
    );
    setSenderIdsProceeding(false);
    if (response) {
      toastFlashMessage(
        `Sender ID ${deletingSenderId.senderId} successfully deleted`,
        'success'
      );
      reloadSenderIds();

      if (hasTrademark) {
        reloadTrademarks?.();
      }
    }
  };

  const senderIdsTableData = useMemo(() => {
    const iconContainerStyles = {
      padding: '0 2px',
      color: 't.black80',
      fontSize: 'H400',
      cursor: 'pointer',
    };
    const renderTrademarkCol = (s: SenderId) => {
      if (!s.trademarkUid) {
        return <Badge type="info">Pending Trademark Assignment</Badge>;
      }
      const t = trademarks.find((t) => t.uid === s.trademarkUid);
      return (
        <div>
          {t?.trademarkText || s.trademarkUid}
          {t?.outcome?.toLowerCase() === 'expired' && (
            <Badge type="error" sx={{ ml: '8px' }}>
              Expired
            </Badge>
          )}
        </div>
      );
    };
    return senderIds.map((senderId) => ({
      senderId: (
        <span data-testid="senderId">{senderId.senderId || 'N/A'}</span>
      ),
      associatedDcaList: (
        <Flex
          data-testid="associatedDcaList"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          {senderId.associatedDcaList
            ? senderId.associatedDcaList
                .split(',')
                .map((dca) => dca.trim())
                .join(', ')
            : 'N/A'}
          <Flex sx={{ gap: '8px', padding: '0 16px' }}>
            <RoleGuard feature="brandDetail.editSenderId">
              <Box
                sx={{
                  display: 'block',
                  ...iconContainerStyles,
                }}
                onClick={() => onEditClick?.(senderId)}
                data-testid="editSenderId"
              >
                <FontAwesomeIcon icon={faPen} />
              </Box>
            </RoleGuard>
            <RoleGuard feature="brandDetail.deleteSenderId">
              <Box
                sx={{
                  display:
                    senderIdsLoading || senderIdsProceeding ? 'none' : 'block',
                  ...iconContainerStyles,
                }}
                onClick={() => {
                  setDeletingSenderId(senderId);
                }}
                data-testid="deleteSenderId"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Box>
            </RoleGuard>
          </Flex>
        </Flex>
      ),
      protectedSenderIdList: (
        <span data-testid="protectedSenderIdList">
          {senderId.protectedSenderIdList?.replaceAll(',', ', ') || 'N/A'}
        </span>
      ),
      trademark: renderTrademarkCol(senderId),
    }));
  }, [senderIds, senderIdsLoading, senderIdsProceeding, trademarks]);

  const handleLoaDownload = async (loa: Loa) => {
    setLoasProceeding(true);
    const response = await brandController.downloadLoa(brandUid, loa.docUid);
    if (response) {
      const file = new File([response.data], response.filename || 'LOA.pdf', {
        type: 'application/pdf',
      });
      generateAndDownloadFile(file);
    }
    setLoasProceeding(false);
  };

  const handleLoaPreview = async (loa: Loa) => {
    setPreviewLoa(loa);
    setPreviewType(PreviewType.Preview);
  };

  const allSenderIdsHaveVerifiedTrademark = useMemo(() => {
    return (
      senderIds.length > 0 &&
      senderIds.every((senderId) => {
        if (!senderId.trademarkUid) return false;
        const trademark = trademarks.find(
          (t) => t.uid === senderId.trademarkUid
        );
        return trademark?.outcome?.toLowerCase() === 'verified';
      })
    );
  }, [senderIds, trademarks]);

  const isAuthplusVerified = Boolean(brand?.businessContactEmailVerifiedDate);
  const isBrandVerified =
    brand?.identityStatus?.toLowerCase() === 'verified' ||
    brand?.identityStatus?.toLowerCase() === 'vetted_verified';
  const canGenerateLoa =
    !readOnly &&
    !loasProceeding &&
    isAuthplusVerified &&
    isBrandVerified &&
    allSenderIdsHaveVerifiedTrademark;

  const loasTableData = useMemo(() => {
    return [...loas].map((loa) => ({
      fileName:
        loa.status === 'COMPLETED' ? (
          <RoleGuard
            feature="brandDetail.downloadLoa"
            render={(authorized) => (
              <Button
                size="sm"
                disabled={!authorized}
                shape="square"
                onClick={() => handleLoaDownload(loa)}
                data-testid="downloadLoa"
              >
                Download LOA
              </Button>
            )}
          />
        ) : (
          <span data-testid="fileName">N/A</span>
        ),
      status: humanize(loa.status) ? (
        <Badge type={getBadgeTypeByStatus(loa.status)} data-testid="status">
          {humanize(loa.status)}
        </Badge>
      ) : (
        <span data-testid="status">N/A</span>
      ),
      createDate: (
        <span data-testid="createDate">
          {convertTimeWithTimezone(loa.createDate, undefined, 'MM/DD/YYYY') ||
            'N/A'}
        </span>
      ),
      completionDate: (
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Box data-testid="signDate">
            {loa.signDate
              ? convertTimeWithTimezone(loa.signDate, undefined, 'MM/DD/YYYY')
              : 'N/A'}
          </Box>
          <MoreButton
            menuClass="tcr-menu"
            data={loa}
            items={[
              {
                disabled:
                  !hasPermission('brandDetail.downloadLoa') ||
                  loa.status !== 'COMPLETED',
                label: 'Download',
                onClick: handleLoaDownload,
              },
              {
                disabled: !hasPermission('brandDetail.previewLoa'),
                label: 'Preview',
                onClick: handleLoaPreview,
              },
            ]}
            data-testid="showMore"
          />
        </Flex>
      ),
    }));
  }, [loas]);

  const handleLoaCreate = async () => {
    setLoasProceeding(true);
    const loa = await brandController.createLoa(brandUid);
    if (loa) {
      setPreviewLoa(loa);
      setPreviewType(PreviewType.Send);
    }
    setLoasProceeding(false);
  };

  return (
    <Flex sx={{ flexDirection: 'column', gap: '24px' }}>
      <DetailsSection
        description={
          'Sender ID must be completed before sending a letter of authorization. All Sender ID information entered\nhere will be populated in the letter of authorization. A maximum of 5 Sender IDs to Allow may be submitted per Brand.'
        }
      >
        <Table
          disableHover
          loading={senderIdsLoading || senderIdsProceeding}
          headRows={SenderIdsTableHeader}
          data={senderIdsTableData}
          emptyState="no sender IDs to view"
          emptyBlockHeight="180px"
        />
        {deletingSenderId && (
          <MessageBar
            type="error"
            title={`Delete Sender ID, ${deletingSenderId.senderId}?`}
            showClose={false}
            primaryAction={{
              label: 'Delete Sender ID',
              onClick: () => {
                deleteSenderId();
              },
            }}
            secondaryAction={{
              label: 'Cancel',
              onClick: () => setDeletingSenderId(null),
            }}
            data-testid="deleteSenderIdConfirmation"
          >
            This action cannot be undone. This Sender ID will be permanently
            removed from this brand's registry profile.
            <br />
            <br />
            <b>Important Note on Signed LOAs</b>
            <br />
            Deleting this Sender ID row from the portal will not dynamically
            update or revoke any previously signed Letters of Authorization
            (LOAs). The signed LOA remains the official source of truth on the
            carrier network. If this Sender ID row was included in a signed LOA
            and your intention is to remove it entirely, you must generate,
            sign, and submit a new LOA.
          </MessageBar>
        )}
      </DetailsSection>
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '24px',
        }}
      >
        <RoleGuard feature="brandDetail.addSenderId">
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="secondary"
            disabled={
              readOnly ||
              senderIds.length >= MaxSenderIdCount ||
              senderIdsProceeding
            }
            onClick={() => onSenderIdAdd?.(MaxSenderIdCount - senderIds.length)}
            data-testid="addSenderId"
          >
            Add Sender ID
          </Button>
        </RoleGuard>
      </Flex>
      <Divider />
      <DetailsSection title="Letter of Authorization" icon={faFileLock}>
        <Table
          disableHover
          loading={loasLoading || loasProceeding}
          headRows={LoasTableHeader}
          data={loasTableData}
          emptyState="no LOAs to view"
          emptyBlockHeight="180px"
        />
      </DetailsSection>
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '24px',
        }}
      >
        <RoleGuard feature="brandDetail.generateLoa">
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="secondary"
            disabled={!canGenerateLoa}
            onClick={handleLoaCreate}
            data-testid="generateLetter"
          >
            Generate Letter
          </Button>
        </RoleGuard>
      </Flex>
      {previewLoa &&
        createPortal(
          <LoaPreviewModal
            readOnly={previewType !== PreviewType.Send}
            brandUid={brandUid}
            docUid={previewLoa.docUid}
            onCancel={() => setPreviewLoa(undefined)}
            onSubmit={() => {
              setPreviewLoa(undefined);
              reloadLoas();
            }}
          />,
          document.body
        )}
    </Flex>
  );
};

export default SenderIdTab;
